export type FlowStepId =
  | "summary"
  | "contract"
  | "capture-card"
  | "payment-method"
  | "payment"
  | "alerts"
  | "result";

/** What the contract and the customer's choices so far say about the flow. */
export interface FlowProgressGates {
  isClientDataEmpty: boolean;
  isContractSigned: boolean;
  isCardCaptureOnly: boolean;
  /** A plan is picked on screen, or the contract already carries one. */
  hasPaymentPlan: boolean;
  /** A card plan is picked and « Suivant » was pressed on it. */
  hasChosenOnlinePlan: boolean;
  /** An active subscription, or the contract is paid in full. */
  hasSettledPayment: boolean;
  /** A card-on-file contract has a card saved. */
  hasCardOnFile: boolean;
}

export interface FlowStepProgress {
  isDisabled: boolean;
  isComplete: boolean;
}

/**
 * Whether each step can be opened, and whether it is done.
 *
 * A plan picked on screen is only a choice: the company learns of it when « Confirmer »
 * subscribes the contract to it (off platform) or when the first payment goes through
 * (by card). Until then nothing after the plan step opens but the card payment, and that
 * one only once « Suivant » is pressed — opening them on the pick alone let a customer
 * finish the flow without ever agreeing to a plan (FD-7986). A card-on-file contract has
 * no plan; its card being saved plays the same part.
 *
 * Each step is ticked as soon as it is done, so the customer can see what is left.
 */
export const flowStepProgress = ({
  isClientDataEmpty,
  isContractSigned,
  isCardCaptureOnly,
  hasPaymentPlan,
  hasChosenOnlinePlan,
  hasSettledPayment,
  hasCardOnFile,
}: FlowProgressGates): Record<FlowStepId, FlowStepProgress> => {
  const hasConfirmedPlan = hasPaymentPlan && hasSettledPayment;
  const isPaymentAgreed = isCardCaptureOnly ? hasCardOnFile : hasConfirmedPlan;

  return {
    summary: { isDisabled: false, isComplete: !isClientDataEmpty },
    contract: { isDisabled: isClientDataEmpty, isComplete: isContractSigned },
    "capture-card": { isDisabled: !isContractSigned, isComplete: hasCardOnFile },
    "payment-method": { isDisabled: !isContractSigned, isComplete: hasConfirmedPlan },
    payment: {
      isDisabled: !isContractSigned || !(hasChosenOnlinePlan || hasSettledPayment),
      isComplete: hasSettledPayment,
    },
    alerts: { isDisabled: isClientDataEmpty || !isPaymentAgreed, isComplete: false },
    result: { isDisabled: !isContractSigned || !isPaymentAgreed, isComplete: false },
  };
};
